<script setup>
import { computed, ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import MessageFormatter from 'shared/helpers/MessageFormatter';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ReactionDisplay from './ReactionDisplay.vue';
import EmojiReactionPicker from './EmojiReactionPicker.vue';
import PollDisplay from './PollDisplay.vue';
import ConversationPreviewCard from './ConversationPreviewCard.vue';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  currentUserId: {
    type: Number,
    required: true,
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
  inThread: {
    type: Boolean,
    default: false,
  },
  groupWithPrevious: {
    type: Boolean,
    default: false,
  },
  groupWithNext: {
    type: Boolean,
    default: false,
  },
  hasThreadDraft: {
    type: Boolean,
    default: false,
  },
  isGroupChannel: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'edit',
  'delete',
  'reply',
  'openThread',
  'addReaction',
  'removeReaction',
  'pin',
  'unpin',
  'vote',
  'unvote',
]);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const bubbleRef = ref(null);
const actionsOpen = ref(false);

onClickOutside(bubbleRef, () => {
  actionsOpen.value = false;
});

function isFinePointer() {
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function toggleActionsOnTouch(event) {
  if (isFinePointer()) return;
  if (event.target.closest('[data-message-actions]')) return;
  actionsOpen.value = !actionsOpen.value;
}

const senderName = computed(() => {
  return props.message.sender?.name || t('INTERNAL_CHAT.MESSAGE.DELETED_USER');
});

const senderAvatar = computed(() => {
  return props.message.sender?.avatar_url || '';
});

const senderAvailability = computed(() => {
  const senderId = props.message.sender?.id;
  if (!senderId) return null;
  const agent = store.getters['agents/getAgentById'](senderId);
  return agent?.availability_status || null;
});

const timestamp = computed(() => {
  const createdAt = props.message.created_at;
  if (!createdAt) return '';
  const unixTime =
    typeof createdAt === 'number'
      ? createdAt
      : Math.floor(new Date(createdAt).getTime() / 1000);
  return messageTimestamp(unixTime, 'HH:mm');
});

const isOwnMessage = computed(() => {
  return props.message.sender?.id === props.currentUserId;
});

const SENDER_NAME_COLORS = {
  light: ['#C2298A', '#99543A', '#60646C', '#008573', '#4747C2', '#3A5BC7'],
  dark: ['#FF8DCC', '#FFA366', '#ADB1B8', '#0BD8B6', '#A19EFF', '#9EB1FF'],
};

const showSenderName = computed(() => {
  return (
    props.isGroupChannel &&
    !isOwnMessage.value &&
    !props.groupWithPrevious &&
    !props.message.content_attributes?.deleted
  );
});

const senderNameStyle = computed(() => {
  if (!showSenderName.value) return {};
  const name = senderName.value || '';
  const index = name.length % SENDER_NAME_COLORS.light.length;
  return {
    color: SENDER_NAME_COLORS.light[index],
    '--dark-sender-color': SENDER_NAME_COLORS.dark[index],
  };
});

const showIncomingAvatar = computed(() => {
  return props.isGroupChannel && !isOwnMessage.value && !props.groupWithNext;
});

const showIncomingAvatarSpacer = computed(() => {
  return props.isGroupChannel && !isOwnMessage.value && props.groupWithNext;
});

const bubbleClass = computed(() => {
  const classes = [];
  if (isOwnMessage.value) {
    classes.push(
      'bg-n-solid-blue text-n-slate-12 rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm'
    );
    if (props.groupWithPrevious) {
      classes.push('ltr:rounded-tr-sm rtl:rounded-tl-sm');
    }
  } else {
    classes.push(
      'bg-n-slate-4 text-n-slate-12 rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm'
    );
    if (props.groupWithPrevious) {
      classes.push('ltr:rounded-tl-sm rtl:rounded-tr-sm');
    }
  }
  return classes;
});

const showMeta = computed(() => !props.groupWithNext);

const isEdited = computed(() => {
  return !!props.message.content_attributes?.edited_at;
});

const isDeleted = computed(() => {
  return !!props.message.content_attributes?.deleted;
});

const isPoll = computed(() => {
  return props.message.content_type === 'poll';
});

const isPinned = computed(() => {
  return !!props.message.content_attributes?.pinned;
});

const threadReplyCount = computed(() => {
  return props.message.replies_count || 0;
});

const canEdit = computed(() => {
  return isOwnMessage.value && !isDeleted.value && !isPoll.value;
});

const canDelete = computed(() => {
  return (isOwnMessage.value || props.isAdmin) && !isDeleted.value;
});

const canPin = computed(() => {
  return !isDeleted.value;
});

const messageContent = computed(() => {
  if (isDeleted.value) {
    return t('INTERNAL_CHAT.MESSAGE.DELETED');
  }
  return props.message.content || '';
});

const renderedContent = computed(() => {
  if (isDeleted.value) return '';
  const formatter = new MessageFormatter(props.message.content || '');
  return formatter.formattedMessage;
});

const reactions = computed(() => {
  return props.message.reactions || [];
});

const conversationRefs = computed(() => {
  if (isDeleted.value || !props.message.content) return [];
  const matches = props.message.content.matchAll(
    /mention:\/\/conversation\/(\d+)\//g
  );
  return [...new Set([...matches].map(m => m[1]))];
});

const isConversationUnfurlOnly = computed(() => {
  if (!conversationRefs.value.length) return false;
  const leftover = (props.message.content || '')
    .replace(/\[[^\]]*\]\(mention:\/\/conversation\/\d+\/[^)]+\)/g, '')
    .trim();
  return leftover.length === 0;
});

function handleContentClick(event) {
  const mention = event.target.closest('.prosemirror-mention-conversation');
  if (!mention) return;
  const displayId = mention.dataset.conversationId;
  if (!displayId) return;
  const url = frontendURL(
    conversationUrl({ accountId: accountId.value, id: displayId })
  );
  if (event.ctrlKey || event.metaKey) {
    window.open(url, '_blank');
  } else {
    router.push(url);
  }
}

function attachmentFileName(attachment) {
  if (attachment.file_url) {
    const url = attachment.file_url.split('?')[0];
    const name = decodeURIComponent(url.split('/').pop());
    if (name && name !== 'null') return name;
  }
  const ext = attachment.extension ? `.${attachment.extension}` : '';
  return `${attachment.file_type || 'file'}${ext}`;
}

const attachments = computed(() => {
  if (isDeleted.value) return [];
  return props.message.attachments || [];
});

const bubbleWidthClass = computed(() => {
  if (
    conversationRefs.value.length ||
    isPoll.value ||
    attachments.value.length
  ) {
    return 'w-full';
  }
  return 'w-fit';
});

const hasBubbleBody = computed(() => {
  if (isDeleted.value || isPoll.value || attachments.value.length) return true;
  return (
    !isConversationUnfurlOnly.value && !!(props.message.content || '').trim()
  );
});

const deleteDialogRef = ref(null);

function handleEdit() {
  emit('edit', props.message);
}

function handleDelete() {
  deleteDialogRef.value?.open();
}

function confirmDelete() {
  emit('delete', props.message);
  deleteDialogRef.value?.close();
}

function handleReply() {
  emit('reply', props.message);
}

function handleOpenThread() {
  emit('openThread', props.message);
}

function handlePin() {
  if (isPinned.value) {
    emit('unpin', props.message);
  } else {
    emit('pin', props.message);
  }
}

function handleCopyLink() {
  const baseUrl = window.chatwootConfig?.hostURL || window.location.origin;
  const path = window.location.pathname;
  const params = [`messageId=${props.message.id}`];
  if (props.message.parent_id) {
    params.push(`parentId=${props.message.parent_id}`);
  }
  const url = `${baseUrl}${path}?${params.join('&')}`;
  copyTextToClipboard(url);
  useAlert(t('INTERNAL_CHAT.MESSAGE.LINK_COPIED'));
}

function handleAddReaction(emoji) {
  emit('addReaction', { messageId: props.message.id, emoji });
}

function handleRemoveReaction(reactionId) {
  emit('removeReaction', {
    messageId: props.message.id,
    reactionId,
  });
}

function handleVote(payload) {
  emit('vote', payload);
}

function handleUnvote(payload) {
  emit('unvote', payload);
}
</script>

<template>
  <div
    ref="bubbleRef"
    class="group relative flex w-full min-w-0 px-5 lg:px-6"
    :class="[
      isOwnMessage ? 'justify-end' : 'justify-start',
      groupWithPrevious ? 'pt-0.5' : 'pt-2',
      showMeta ? 'pb-2' : 'pb-0.5',
    ]"
    @click="toggleActionsOnTouch"
  >
    <div v-if="showIncomingAvatar" class="me-2 flex shrink-0 items-end">
      <Avatar
        :name="senderName"
        :src="senderAvatar"
        :size="24"
        :status="senderAvailability"
        rounded-full
      />
    </div>
    <div v-else-if="showIncomingAvatarSpacer" class="me-2 w-6 shrink-0" />

    <div
      class="flex min-w-0 w-full max-w-[min(85%,32rem)] flex-col"
      :class="isOwnMessage ? 'items-end' : 'items-start'"
    >
      <span
        v-if="showSenderName"
        class="mb-0.5 inline-block px-1 text-xs font-medium dark:!text-[var(--dark-sender-color)]"
        :style="senderNameStyle"
      >
        {{ senderName }}
      </span>

      <div class="relative" :class="bubbleWidthClass">
        <div
          v-if="hasBubbleBody"
          class="px-4 py-2.5 text-sm"
          :class="bubbleClass"
        >
          <div v-if="isPoll && !isDeleted">
            <PollDisplay
              :message="message"
              :current-user-id="currentUserId"
              :is-admin="isAdmin"
              @vote="handleVote"
              @unvote="handleUnvote"
            />
          </div>
          <template v-else>
            <div
              v-if="isDeleted"
              class="flex items-center gap-1.5 text-n-slate-11"
            >
              <Icon icon="i-lucide-trash-2" class="size-3.5 shrink-0" />
              <span class="italic">{{ messageContent }}</span>
            </div>
            <div
              v-else-if="!isConversationUnfurlOnly"
              class="break-words [&_.prosemirror-mention-node]:font-semibold [&_.prosemirror-mention-conversation]:cursor-pointer"
              @click="handleContentClick"
            >
              <div
                v-dompurify-html="renderedContent"
                class="prose prose-bubble inline"
              />
            </div>
          </template>

          <div v-if="attachments.length" class="mt-2 flex flex-wrap gap-2">
            <template v-for="attachment in attachments" :key="attachment.id">
              <a
                v-if="attachment.file_type === 'image'"
                :href="attachment.file_url || attachment.external_url"
                target="_blank"
                rel="noopener noreferrer"
                class="block overflow-hidden rounded-lg"
              >
                <img
                  :src="attachment.file_url || attachment.external_url"
                  class="max-h-60 max-w-full object-cover sm:max-w-xs"
                  loading="lazy"
                />
              </a>
              <a
                v-else
                :href="attachment.file_url || attachment.external_url"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-1.5 rounded-lg bg-n-alpha-black1 px-2.5 py-1.5 text-xs hover:bg-n-alpha-2"
              >
                <Icon icon="i-lucide-paperclip" class="size-3.5" />
                <span class="max-w-48 truncate">
                  {{ attachmentFileName(attachment) }}
                </span>
              </a>
            </template>
          </div>
        </div>

        <ConversationPreviewCard
          v-for="displayId in conversationRefs"
          :key="`conv-${displayId}`"
          :display-id="displayId"
          :account-id="accountId"
          :is-own-message="isOwnMessage"
        />

        <div
          v-if="!isDeleted"
          data-message-actions
          class="absolute top-1 z-10 flex items-center gap-0.5 rounded-full border border-n-slate-6 bg-n-solid-2 p-0.5 shadow-sm transition-opacity"
          :class="[
            actionsOpen
              ? 'opacity-100'
              : 'opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100',
            isOwnMessage ? 'end-1' : 'start-1',
          ]"
        >
          <EmojiReactionPicker
            :reactions="reactions"
            :current-user-id="currentUserId"
            @select="handleAddReaction"
            @remove="handleRemoveReaction"
          />
          <button
            v-if="!inThread"
            class="flex items-center justify-center rounded-full p-1 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            :title="t('INTERNAL_CHAT.MESSAGE.REPLY')"
            @click="handleReply"
          >
            <Icon icon="i-lucide-reply" class="size-4" />
          </button>
          <button
            class="flex items-center justify-center rounded-full p-1 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            :title="t('INTERNAL_CHAT.MESSAGE.COPY_LINK')"
            @click="handleCopyLink"
          >
            <Icon icon="i-lucide-link" class="size-4" />
          </button>
          <button
            v-if="canPin && !inThread"
            class="flex items-center justify-center rounded-full p-1 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            :title="
              isPinned
                ? t('INTERNAL_CHAT.PIN.UNPIN')
                : t('INTERNAL_CHAT.PIN.PIN')
            "
            @click="handlePin"
          >
            <Icon
              :icon="isPinned ? 'i-lucide-pin-off' : 'i-lucide-pin'"
              class="size-4"
            />
          </button>
          <button
            v-if="canEdit"
            class="flex items-center justify-center rounded-full p-1 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            :title="t('INTERNAL_CHAT.MESSAGE.EDIT')"
            @click="handleEdit"
          >
            <Icon icon="i-lucide-pencil" class="size-4" />
          </button>
          <button
            v-if="canDelete"
            class="flex items-center justify-center rounded-full p-1 text-n-slate-11 hover:bg-n-ruby-3 hover:text-n-ruby-11"
            :title="t('INTERNAL_CHAT.MESSAGE.DELETE')"
            @click="handleDelete"
          >
            <Icon icon="i-lucide-trash-2" class="size-4" />
          </button>
        </div>
      </div>

      <ReactionDisplay
        :reactions="reactions"
        :current-user-id="currentUserId"
        @remove="handleRemoveReaction"
      />

      <div
        v-if="showMeta"
        class="mt-1 flex items-center gap-1.5 px-1 text-[11px] tabular-nums text-n-slate-10"
        :class="isOwnMessage ? 'justify-end' : 'justify-start'"
      >
        <time>{{ timestamp }}</time>
        <span v-if="isEdited">{{ t('INTERNAL_CHAT.MESSAGE.EDITED') }}</span>
        <Icon
          v-if="isPinned"
          icon="i-lucide-pin"
          class="size-3 text-n-amber-11"
          :title="t('INTERNAL_CHAT.PIN.PINNED_MESSAGE')"
        />
      </div>

      <div
        v-if="
          !inThread &&
          ((message.parent_id && !groupWithNext) ||
            threadReplyCount > 0 ||
            hasThreadDraft)
        "
        class="mt-1 flex items-center gap-2 px-1"
      >
        <button
          v-if="message.parent_id && !groupWithNext"
          class="flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
          @click="handleOpenThread"
        >
          <Icon icon="i-lucide-message-square" class="size-3" />
          {{ t('INTERNAL_CHAT.THREAD.TITLE') }}
        </button>
        <button
          v-else-if="threadReplyCount > 0"
          class="flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
          @click="handleOpenThread"
        >
          <Icon icon="i-lucide-message-square" class="size-3" />
          {{ t('INTERNAL_CHAT.THREAD.REPLIES', { count: threadReplyCount }) }}
        </button>
        <button
          v-if="hasThreadDraft"
          class="flex items-center gap-1 text-xs font-medium text-n-amber-11 hover:underline"
          @click="handleOpenThread"
        >
          <Icon icon="i-lucide-file-edit" class="size-3" />
          {{ t('INTERNAL_CHAT.DRAFT.LABEL') }}
        </button>
      </div>
    </div>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('INTERNAL_CHAT.MESSAGE.DELETE')"
      :description="t('INTERNAL_CHAT.MESSAGE.CONFIRM_DELETE')"
      :confirm-button-label="t('INTERNAL_CHAT.MESSAGE.DELETE')"
      @confirm="confirmDelete"
    />
  </div>
</template>
